import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { oportunidadService, OportunidadCreate, OportunidadUpdate } from '../../services/oportunidadService';
import { sucursalService } from '../../services/sucursalService';
import { clienteService } from '../../services/clienteService';
import { leadService } from '../../services/leadService';

const OportunidadForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  // Obtener parámetros de URL si vienen de un lead
  const leadIdFromUrl = searchParams.get('leadId');
  const nombreContactoFromUrl = searchParams.get('nombreContacto');
  const emailContactoFromUrl = searchParams.get('emailContacto');
  const telefonoContactoFromUrl = searchParams.get('telefonoContacto');
  const empresaContactoFromUrl = searchParams.get('empresaContacto');
  const cargoContactoFromUrl = searchParams.get('cargoContacto');
  const direccionContactoFromUrl = searchParams.get('direccionContacto');
  const origenFromUrl = searchParams.get('origen');
  const valorEstimadoFromUrl = searchParams.get('valorEstimado');

  const { data: oportunidadData } = useQuery(
    ['oportunidad', id],
    () => oportunidadService.getById(Number(id!)),
    { enabled: isEdit }
  );

  const { data: etapasData } = useQuery(
    'pipeline-etapas',
    () => oportunidadService.getPipelineEtapas()
  );

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data: clientesData } = useQuery(
    'clientes',
    () => clienteService.getAll(1, 100)
  );

  const { data: leadsData } = useQuery(
    'leads',
    () => leadService.getAll(1, 100)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<OportunidadCreate | OportunidadUpdate>({
    defaultValues: isEdit && oportunidadData?.data.oportunidad ? {
      nombre: oportunidadData.data.oportunidad.nombre,
      nombreContacto: oportunidadData.data.oportunidad.nombreContacto,
      emailContacto: oportunidadData.data.oportunidad.emailContacto,
      telefonoContacto: oportunidadData.data.oportunidad.telefonoContacto,
      empresaContacto: oportunidadData.data.oportunidad.empresaContacto,
      cargoContacto: oportunidadData.data.oportunidad.cargoContacto,
      direccionContacto: oportunidadData.data.oportunidad.direccionContacto,
      origen: oportunidadData.data.oportunidad.origen,
      fuente: oportunidadData.data.oportunidad.fuente,
      descripcion: oportunidadData.data.oportunidad.descripcion,
      etapaId: oportunidadData.data.oportunidad.etapaId,
      valor: typeof oportunidadData.data.oportunidad.valor === 'string' 
        ? parseFloat(oportunidadData.data.oportunidad.valor) 
        : oportunidadData.data.oportunidad.valor,
      probabilidad: oportunidadData.data.oportunidad.probabilidad,
      fechaCierreEstimada: oportunidadData.data.oportunidad.fechaCierreEstimada,
      sucursalId: oportunidadData.data.oportunidad.sucursalId,
      tipoOportunidad: oportunidadData.data.oportunidad.tipoOportunidad,
      competencia: oportunidadData.data.oportunidad.competencia,
      leadId: oportunidadData.data.oportunidad.leadId,
      clienteId: oportunidadData.data.oportunidad.clienteId,
      notas: oportunidadData.data.oportunidad.notas
    } : (leadIdFromUrl ? {
      // Pre-llenar desde lead si viene de URL
      leadId: Number(leadIdFromUrl),
      nombreContacto: nombreContactoFromUrl || '',
      emailContacto: emailContactoFromUrl || '',
      telefonoContacto: telefonoContactoFromUrl || '',
      empresaContacto: empresaContactoFromUrl || '',
      cargoContacto: cargoContactoFromUrl || '',
      direccionContacto: direccionContactoFromUrl || '',
      origen: origenFromUrl || 'manual',
      valor: valorEstimadoFromUrl ? Number(valorEstimadoFromUrl) : 0,
      probabilidad: 0
    } : {
      origen: 'manual',
      probabilidad: 0
    })
  });

  const emailContacto = watch('emailContacto');
  const telefonoContacto = watch('telefonoContacto');

  const createMutation = useMutation(oportunidadService.create, {
    onSuccess: (data) => {
      toast.success('Oportunidad creada correctamente');
      queryClient.invalidateQueries('oportunidades');
      queryClient.invalidateQueries('leads');
      
      // Si viene de un lead, volver al lead
      if (leadIdFromUrl) {
        navigate(`/crm/leads/${leadIdFromUrl}`);
      } else {
        navigate(`/crm/oportunidades/${data.data.oportunidad.id}`);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear oportunidad');
    }
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: OportunidadUpdate }) => oportunidadService.update(id, data),
    {
      onSuccess: () => {
        toast.success('Oportunidad actualizada correctamente');
        queryClient.invalidateQueries('oportunidades');
        navigate(`/crm/oportunidades/${id}`);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar oportunidad');
      }
    }
  );

  const onSubmit = (data: any) => {
    // Validar que al menos email o teléfono estén presentes
    if (!data.emailContacto && !data.telefonoContacto) {
      toast.error('Debe proporcionar al menos un email o teléfono del contacto');
      return;
    }

    // Si viene de un lead, asegurar que el leadId esté incluido
    if (leadIdFromUrl && !data.leadId) {
      data.leadId = Number(leadIdFromUrl);
    }

    // Obtener la primera etapa si no se proporciona
    if (!data.etapaId && etapasData?.data.etapas && etapasData.data.etapas.length > 0) {
      data.etapaId = etapasData.data.etapas[0].id;
    }

    if (isEdit) {
      updateMutation.mutate({ id: Number(id!), data });
    } else {
      createMutation.mutate(data);
    }
  };

  const origenes = ['manual', 'web', 'referido', 'evento', 'redes_sociales', 'email', 'telefono', 'otro'];
  const tiposOportunidad = [
    { value: 'nuevo_cliente', label: 'Nuevo Cliente' },
    { value: 'upsell', label: 'Upsell' },
    { value: 'renovacion', label: 'Renovación' },
    { value: 'cross_sell', label: 'Cross Sell' },
    { value: 'referido', label: 'Referido' },
    { value: 'otro', label: 'Otro' }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate(isEdit ? `/crm/oportunidades/${id}` : '/crm/oportunidades')}
            className="text-gray-600 hover:text-gray-800 mb-2"
          >
            ← Volver
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            {isEdit ? 'Editar Oportunidad' : 'Nueva Oportunidad'}
          </h1>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Información de la Oportunidad */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Información de la Oportunidad</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la Oportunidad *
                </label>
                <input
                  {...register('nombre', { required: 'Nombre de la oportunidad es requerido' })}
                  placeholder="Ej: Implementación POS para Empresa XYZ"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.nombre ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.nombre && (
                  <p className="text-red-500 text-sm mt-1">{errors.nombre.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Origen *
                </label>
                <select
                  {...register('origen', { required: 'Origen es requerido' })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.origen ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                >
                  {origenes.map((origen) => (
                    <option key={origen} value={origen}>
                      {origen.charAt(0).toUpperCase() + origen.slice(1).replace('_', ' ')}
                    </option>
                  ))}
                </select>
                {errors.origen && (
                  <p className="text-red-500 text-sm mt-1">{errors.origen.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fuente
                </label>
                <input
                  {...register('fuente')}
                  placeholder="Fuente más detallada"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Estimado *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  {...register('valor', { 
                    required: 'Valor estimado es requerido',
                    valueAsNumber: true,
                    min: { value: 0, message: 'El valor debe ser positivo' }
                  })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.valor ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.valor && (
                  <p className="text-red-500 text-sm mt-1">{errors.valor.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de Cierre Estimada *
                </label>
                <input
                  type="date"
                  {...register('fechaCierreEstimada', { required: 'Fecha de cierre estimada es requerida' })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.fechaCierreEstimada ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.fechaCierreEstimada && (
                  <p className="text-red-500 text-sm mt-1">{errors.fechaCierreEstimada.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Etapa del Pipeline *
                </label>
                <select
                  {...register('etapaId', { 
                    required: 'Etapa es requerida',
                    valueAsNumber: true
                  })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.etapaId ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                >
                  <option value={0}>Selecciona etapa</option>
                  {etapasData?.data.etapas.map((etapa) => (
                    <option key={etapa.id} value={etapa.id}>
                      {etapa.nombre}
                    </option>
                  ))}
                </select>
                {errors.etapaId && (
                  <p className="text-red-500 text-sm mt-1">{errors.etapaId.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Oportunidad
                </label>
                <select
                  {...register('tipoOportunidad')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Selecciona tipo</option>
                  {tiposOportunidad.map((tipo) => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sucursal
                </label>
                <select
                  {...register('sucursalId', { valueAsNumber: true })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={0}>Sin sucursal</option>
                  {sucursalesData?.data.sucursales.map((sucursal) => (
                    <option key={sucursal.id} value={sucursal.id}>
                      {sucursal.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Competencia
                </label>
                <input
                  {...register('competencia')}
                  placeholder="Ej: Salesforce, Zoho"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  {...register('descripcion')}
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Información del Contacto */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Información del Contacto</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Contacto *
                </label>
                <input
                  {...register('nombreContacto', { required: 'Nombre del contacto es requerido' })}
                  placeholder="Ej: Juan Pérez"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.nombreContacto ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.nombreContacto && (
                  <p className="text-red-500 text-sm mt-1">{errors.nombreContacto.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email del Contacto *
                </label>
                <input
                  type="email"
                  {...register('emailContacto', {
                    validate: (value) => {
                      if (!value && !telefonoContacto) {
                        return 'Debe proporcionar al menos un email o teléfono';
                      }
                      return true;
                    }
                  })}
                  placeholder="contacto@empresa.com"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.emailContacto ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.emailContacto && (
                  <p className="text-red-500 text-sm mt-1">{errors.emailContacto.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono del Contacto *
                </label>
                <input
                  {...register('telefonoContacto', {
                    validate: (value) => {
                      if (!value && !emailContacto) {
                        return 'Debe proporcionar al menos un email o teléfono';
                      }
                      if (value && !/^\d{10}$/.test(value.replace(/\D/g, ''))) {
                        return 'El teléfono debe tener exactamente 10 dígitos';
                      }
                      return true;
                    }
                  })}
                  placeholder="5551234567"
                  maxLength={10}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.telefonoContacto ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.telefonoContacto && (
                  <p className="text-red-500 text-sm mt-1">{errors.telefonoContacto.message}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Al menos email o teléfono es requerido</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa
                </label>
                <input
                  {...register('empresaContacto')}
                  placeholder="Nombre de la empresa"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cargo
                </label>
                <input
                  {...register('cargoContacto')}
                  placeholder="Cargo del contacto"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <textarea
                  {...register('direccionContacto')}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Relaciones */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Relaciones (Opcional)</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente Relacionado
                </label>
                <select
                  {...register('clienteId', { valueAsNumber: true })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={0}>Sin cliente</option>
                  {clientesData?.data.clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre} {cliente.email ? `- ${cliente.email}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Relacionado
                </label>
                <select
                  {...register('leadId', { valueAsNumber: true })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={0}>Sin lead</option>
                  {leadsData?.data.leads.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.nombre} {lead.email ? `- ${lead.email}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              {...register('notas')}
              rows={4}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate(isEdit ? `/crm/oportunidades/${id}` : '/crm/oportunidades')}
              className="px-6 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading
                ? 'Guardando...'
                : isEdit
                ? 'Actualizar'
                : 'Crear Oportunidad'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OportunidadForm;

